import { Dispatch } from "@reduxjs/toolkit";
import { ItemType } from "antd/es/menu/interface";
import { callNotify } from "../../../..";
import { useTrackEvent } from "../../../../components/AnalyticsProvider/useTrackEvent";
import Icon, { ICON_NAME } from "../../../../components/Icon";
import { openPopup } from "../../../../components/Popup/_components/WrapperPopup";
import { openNote } from "../../../../components/Popup/PopupOpNote";
import { openPage } from "../../../../util/library/Router";
import { selectOpMode } from "../../../../util/store/selectors";
import { getStore } from '../../../../util/store/store';
import {
    ACTION_TYPE,
    aWeekMillisecond,
    CERTAIN_TYPE,
    certainTypes,
    OP_STATUS,
    OPERATION_CONFIRM_STATUS,
    OPERATION_TYPE,
    PATHNAME,
    TIME_PER_STEP
} from "../../../../util/varibles/constants";
import { getStartTimeFromDate } from "../../../../util/varibles/global";
import { IOperation, IOperations, IVessel } from "../../../../util/varibles/interface";
import { checkApprovePermission } from "../../../../util/varibles/permission";
import { loginActions } from "../../../LoginPage/reducer";
import { overviewActions } from "../../../OverviewPage/reducer";
import { PlanOperationState, selectPlan } from "../../constants";
import PopupAvailableTime from "../../Popup/AvailableTime";
import openPopupDuplicate from "../../Popup/Duplicate";
import openPopupInvoiceRelated from "../../Popup/InvoiceRelated";
import { planOpActions } from "../../reducer";
import { FuncOp, getTimeByOpMode } from "../../util/function_operation/constants";
import { funcGlobal, updateTime } from "../../util/function_operation/global";
import {
    handleAddDelay,
    handleApproveOp,
    handleCancelOperation,
    handleDeclineOperation,
    handleDownloadPdf,
    handleSaveOperation,
    handleSendMail,
    handleSendToVessel,
    handleStartOp,
    handleVesselApproveOp,
    handleVesselCancelOp,
    openPopupDuration,
    showPopupDeleteOperation,
} from "./actions";

export enum MENU_OP {
    START = 'start',
    DETAIL = 'detail',
    EDIT = 'edit',
    VESSEL_EDIT = 'vessel-edit',
    SEND_TO_VESSEL = 'send-to-vessel',
    SAVE_OPERATION = 'save-operation',
    DELETE = 'delete',
    VESSEL_DELETE = 'vessel-delete',
    APPROVE = 'approve',
    VESSEL_APPROVE = 'vessel-approve',
    DECLINE = 'decline',
    VESSEL_DECLINE = 'vessel-decline',
    CANCEL = 'cancel',
    VESSEL_CANCEL = 'vessel-cancel',
    ROUTE_DURATION = 'route-duration',
    DELAY = 'delay',
    ADD_NOTE = 'add-note',
    PUSH_CLOSER = 'push-close',
    DUPLICATE = 'duplicate',
    SEND_MAIL = 'send-mail',
    DOWNLOAD_PDF = 'download-pdf',
    CERTAINTY = 'certainty',
    INVOICE_RELATED = 'invoice-related',
}

export interface IMenuOperation {
    name: string,
    icon: ICON_NAME,
    iconSize?: number,
    iconStyle?: React.CSSProperties,
    onClick: any
    isActive: boolean
    danger?: boolean
}

export interface IDataMenuOperation extends IOperation {
    isActive: boolean // Active when the operation is not in the processing or completed state and is not in the past.
}

export interface IPropsMenu {
    dispatch: Dispatch
    operations: IOperations
    trackEvent?: ReturnType<typeof useTrackEvent>

    edit?(): any
}

export type TMenuOp = { key?: string, label?: React.ReactNode, type?: string, children?: TMenuOp[] } | null

export interface IMenuOp {
    [menuId: string]: (data: IDataMenuOperation, props: IPropsMenu) => TMenuOp
}

export const canEditOperationInPast = (data: IOperation) => {
    const { operation_type, status } = data.operation;
    return [OPERATION_TYPE.EVENT, OPERATION_TYPE.SERVICE].includes(operation_type)
        && status !== OP_STATUS.PROCESSING
        && status !== OP_STATUS.FINISHED;
}

export const menuOp: IMenuOp = {
    [MENU_OP.START]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Start',
            icon: ICON_NAME.START,
            onClick: () => handleStartOp(data, props.dispatch),
            isActive: status === OP_STATUS.APPROVED
        })
    },
    [MENU_OP.DETAIL]: (data) => {
        const { tenant_id, id } = data.operation;
        return renderOpt({
            name: 'Operation detail',
            icon: ICON_NAME.OPERATION_DETAIL,
            onClick: () => openPage(`${PATHNAME.OPERATION}/${tenant_id}/${id}`),
            isActive: data.action_type !== OPERATION_CONFIRM_STATUS.NEW
        })
    },
    [MENU_OP.EDIT]: (data, props) => {
        return renderOpt({
            name: 'Edit operation',
            icon: ICON_NAME.EDIT,
            onClick: () => {
                const { edit } = props;
                edit?.()
            },
            isActive: data.isActive,
        })
    },
    [MENU_OP.VESSEL_EDIT]: (data, props) => {
        return renderOpt({
            name: 'View/Edit',
            icon: ICON_NAME.EDIT,
            onClick: props.edit,
            isActive: data.isActive,
        })
    },
    [MENU_OP.SEND_TO_VESSEL]: (data, props) => {
        const startTime = data.activity_log[0].est_start_time;
        const { is_active_vessel } = data.operation;
        const isAllowedByTime = startTime > Date.now() || canEditOperationInPast(data);
        return renderOpt({
            name: 'Send to vessel (and site side)',
            icon: ICON_NAME.SEND_TO_VESSEL,
            onClick: () => {
                const { operations, dispatch } = props;
                handleSendToVessel(data, operations, dispatch, props.trackEvent);
            },
            isActive: (!!data.action_type && (isAllowedByTime || data.operation.status === OP_STATUS.PROCESSING)) || (!is_active_vessel && (data.operation.status === OP_STATUS.SAVED || data.operation.status === OP_STATUS.WAITING))
            // isActive: (!!data.action_type || !is_active_vessel) && (startTime > Date.now() || data.operation.status === OP_STATUS.PROCESSING)
        })
    },
    [MENU_OP.SAVE_OPERATION]: (data, props) => {
        const startTime = data.activity_log[0].est_start_time;
        const { is_active_vessel } = data.operation;
        return renderOpt({
            name: 'Save operation',
            icon: ICON_NAME.SAVE_OPERATION,
            onClick: () => {
                const { operations, dispatch } = props;
                handleSaveOperation(data, operations, dispatch, props.trackEvent);
            },
            isActive: (!!data.action_type || !is_active_vessel) && (startTime > Date.now() || canEditOperationInPast(data))
        })
    },
    [MENU_OP.ADD_NOTE]: (data, props) => {
        const { note, status } = data.operation;
        return renderOpt({
            name: (note || '').length === 0 ? 'Add note' : 'Edit note',
            icon: ICON_NAME.NOTE,
            onClick: () => {
                const moreProps = status !== OP_STATUS.NEW ? { operation: data.operation } : {}
                openNote({
                    ...moreProps,
                    title: `#${data.operation.operation_code}`,
                    note,
                    onSave: (note: string) => {
                        const { id } = data.operation;
                        if (Object.keys(moreProps).length > 0)
                            props.dispatch(planOpActions.updateOpsByWs({
                                data: [{ ...data, operation: { ...data.operation, note } }]
                            }))
                        else
                            props.dispatch(planOpActions.updateOps({
                                update: { [id]: { ...data, operation: { ...data.operation, note } } },
                                isCheck: false
                            }))
                    }
                })
            },
            isActive: true
        })
    },
    [MENU_OP.DELETE]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Delete',
            icon: ICON_NAME.DELETE,
            danger: true,
            onClick: () => showPopupDeleteOperation(data, props, (ids: string[]) => {
                props.dispatch(planOpActions.deleteOps(ids))
                props.dispatch(overviewActions.deleteOps(ids))
            }),
            isActive: status !== OP_STATUS.PROCESSING
        })
    },
    [MENU_OP.VESSEL_DELETE]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Delete',
            icon: ICON_NAME.DELETE,
            danger: true,
            onClick: () => showPopupDeleteOperation(data, props, (ids: string[]) => {
                props.dispatch(overviewActions.deleteOps(ids));
                props.dispatch(loginActions.deleteOpOfVessel(ids));
            }),
            isActive: status !== OP_STATUS.PROCESSING
        })
    },
    [MENU_OP.APPROVE]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Approve',
            icon: ICON_NAME.APPROVE,
            onClick: () => handleApproveOp(data, props.dispatch),
            isActive: status === OP_STATUS.PENDING || status === OP_STATUS.SAVED
        })
    },
    [MENU_OP.VESSEL_APPROVE]: (data, props) => {
        const { status } = data.operation;
        const isAllow = checkApprovePermission();
        return renderOpt({
            name: 'Approve',
            icon: ICON_NAME.APPROVE,
            onClick: () => handleVesselApproveOp(data, props.dispatch),
            isActive: (isAllow && status === OP_STATUS.PENDING) || status === OP_STATUS.WAITING
        })
    },
    [MENU_OP.DECLINE]: (data, props) => {
        const { status } = data.operation;
        const isAllow = checkApprovePermission();
        return renderOpt({
            name: 'Decline',
            icon: ICON_NAME.CANCEL,
            danger: true,
            onClick: () => handleDeclineOperation(data, props.dispatch),
            isActive: (isAllow && status === OP_STATUS.PENDING) || status === OP_STATUS.APPROVED
        })
    },
    [MENU_OP.CANCEL]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Cancel',
            icon: ICON_NAME.CANCEL,
            onClick: () => handleCancelOperation(data, props.dispatch),
            isActive: status === OP_STATUS.SAVED || status === OP_STATUS.WAITING
        })
    },
    [MENU_OP.VESSEL_CANCEL]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Cancel',
            icon: ICON_NAME.CANCEL,
            danger: true,
            onClick: () => handleVesselCancelOp(data, props.dispatch),
            isActive: status === OP_STATUS.WAITING
        })
    },
    [MENU_OP.ROUTE_DURATION]: (data, props) => {
        return renderOpt({
            name: 'Route & Duration',
            icon: ICON_NAME.ROUTE,
            onClick: () => openPopupDuration(data, props.dispatch),
            isActive: data.isActive,
        })
    },
    [MENU_OP.DELAY]: (data, props) => {
        return renderOpt({
            name: 'Add delay time',
            icon: ICON_NAME.ADD_DELAY,
            onClick: () => handleAddDelay(data, props.dispatch),
            isActive: false
            // isActive: data.operation.status === OP_STATUS.PROCESSING 
        })
    },
    [MENU_OP.PUSH_CLOSER]: (data, props) => {
        return renderOpt({
            name: 'Move next to previous operation',
            icon: ICON_NAME.MOVE_OP,
            onClick: () => {
                const opMode = selectOpMode(getStore());
                const { operations } = props;
                const { activity_log } = data;
                const { id: vesselId } = data.vessel;

                const { [opMode]: getTime }: any = getTimeByOpMode;
                const { id } = data.operation;
                const startA = getTime(activity_log[0]) - aWeekMillisecond;
                const finishA = getTime(activity_log[activity_log.length - 1]);

                const newTime = Object.keys(operations).reduce((time: number, key) => {
                    if (operations[key].vessel.id !== vesselId || key === id)
                        return time
                    const startB = getTime(operations[key].activity_log[0]);
                    const max = operations[key].activity_log.length;
                    const finishB = getTime(operations[key].activity_log[max - 1]);

                    if (startB <= finishA && finishB >= startA) {
                        if (time < finishB)
                            return finishB;
                    }

                    return time
                }, startA);

                if (newTime === getTime(activity_log[0])) {
                    callNotify?.error({
                        title: 'Move failed',
                        description: 'There is no operation within previous 7 days to move next to.\n' +
                            '(System only supports to move operation within 7 days)'
                    })
                } else
                    props.dispatch(planOpActions.updateOps({
                        update: {
                            [data.operation.id]: {
                                ...data,
                                activity_log: updateTime(data.activity_log, 0, newTime + TIME_PER_STEP)
                            }
                        }
                    }))
            },
            isActive: data.operation.status !== OP_STATUS.PROCESSING && data.operation.status !== OP_STATUS.CANCELED && data.operation.status !== OP_STATUS.FINISHED
        })
    },
    [MENU_OP.DUPLICATE]: (data: IDataMenuOperation) => {
        return renderOpt({
            icon: ICON_NAME.DUPLICATE,
            name: 'Duplicate operation',
            onClick: () => openPopupDuplicate({ data }),
            isActive: true
        })
    },
    [MENU_OP.SEND_MAIL]: (data: IDataMenuOperation) => {
        const { operation_type, status } = data.operation;
        const isSkipped = (new Set([OPERATION_TYPE.SERVICE, OPERATION_TYPE.SUPPORT])).has(operation_type);

        return renderOpt({
            icon: ICON_NAME.SEND_MAIL,
            name: 'Send email PDF file',
            onClick: () => handleSendMail(data),
            isActive: status !== OP_STATUS.NEW && !isSkipped
        })
    },
    [MENU_OP.DOWNLOAD_PDF]: (data: IDataMenuOperation) => {
        const { operation_type, status } = data.operation;
        const isSkipped = (new Set([OPERATION_TYPE.SERVICE, OPERATION_TYPE.SUPPORT])).has(operation_type);
        return renderOpt({
            icon: ICON_NAME.PDF,
            name: 'Download PDF file',
            onClick: () => handleDownloadPdf(data),
            isActive: status !== OP_STATUS.NEW && !isSkipped
        })
    },
    [MENU_OP.CERTAINTY]: (data, props) => {
        const name = "Set operation's certainty";
        return {
            key: name,
            label: <div className='menu-line'>
                <Icon icon={ICON_NAME.PIN} size={15} />
                <span>{name}</span>
            </div>,
            children: [
                {
                    key: 'certainty-view',
                    popupClassName: "!p-0",
                    className: '!cursor-default !p-0 ',
                    label: <div className='flex'>
                        {certainTypes.map(item => <div
                            key={item.id}
                            className="px-3 py-[3px] flex items-center gap-1 !cursor-pointer first:rounded-l-[4px] last:rounded-r-[4px]"
                            style={{ backgroundColor: item.color }}
                            onClick={() => {
                                props.dispatch(planOpActions.updateOps({
                                    update: {
                                        [data.operation.id]: {
                                            ...data,
                                            operation: { ...data.operation, certain_mode: item.id }
                                        }
                                    }
                                }))
                            }}
                        >
                            <div style={{ color: item.fontColor }}>{item.name}</div>
                            <Icon icon={ICON_NAME.PIN} size={15} style={{ background: item.fontColor }} />
                        </div>)}
                    </div>,
                }
            ]
        }
    },
    [MENU_OP.INVOICE_RELATED]: (data, props) => {
        const { status } = data.operation;
        return renderOpt({
            name: 'Select Invoice related',
            icon: ICON_NAME.MULTIPLE_INVOICE,
            iconSize: 19,
            iconStyle: { marginLeft: -1 },
            onClick: () => {
                openPopupInvoiceRelated({
                    data,
                    operations: props.operations,
                });
            },
            isActive: status !== OP_STATUS.NEW,
        })
    }
}

export enum MENU_CALENDAR {
    ADD_EVENT = 'add-event',
    ADD_AVAILABLE_TIME = 'add-available-time',
    ADD_SERVICE = 'add-service',
    DIVIDE = 'divide',
}

const menuCommon: Record<string, () => { type: string }> = {
    [MENU_CALENDAR.DIVIDE]: () => ({
        type: 'divider',
    })
}

export const menuOptions: Partial<Record<OPERATION_TYPE, (MENU_OP | MENU_CALENDAR)[]>> = {
    [OPERATION_TYPE.HARVEST]: [
        MENU_OP.DETAIL,
        MENU_OP.SAVE_OPERATION,
        MENU_OP.SEND_TO_VESSEL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.PUSH_CLOSER,
        MENU_OP.CERTAINTY,
        MENU_OP.ADD_NOTE,
        MENU_OP.EDIT,
        MENU_OP.INVOICE_RELATED,
        MENU_OP.DELAY,
        MENU_OP.ROUTE_DURATION,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.DOWNLOAD_PDF,
        MENU_OP.SEND_MAIL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.CANCEL,
        MENU_OP.DELETE,
    ],
    [OPERATION_TYPE.TREATMENT]: [
        MENU_OP.DETAIL,
        MENU_OP.SAVE_OPERATION,
        MENU_OP.SEND_TO_VESSEL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.PUSH_CLOSER,
        MENU_OP.CERTAINTY,
        MENU_OP.ADD_NOTE,
        MENU_OP.EDIT,
        MENU_OP.INVOICE_RELATED,
        MENU_OP.DELAY,
        MENU_OP.DUPLICATE,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.DOWNLOAD_PDF,
        MENU_OP.SEND_MAIL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.CANCEL,
        MENU_OP.DELETE
    ],
    [OPERATION_TYPE.TRANSPORT]: [
        MENU_OP.DETAIL,
        MENU_OP.SAVE_OPERATION,
        MENU_OP.SEND_TO_VESSEL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.PUSH_CLOSER,
        MENU_OP.CERTAINTY,
        MENU_OP.ADD_NOTE,
        MENU_OP.EDIT,
        MENU_OP.DELAY,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.DOWNLOAD_PDF,
        MENU_OP.SEND_MAIL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.CANCEL,
        MENU_OP.DELETE
    ],
    [OPERATION_TYPE.EVENT]: [
        MENU_OP.DETAIL,
        MENU_OP.SAVE_OPERATION,
        MENU_OP.SEND_TO_VESSEL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.PUSH_CLOSER,
        MENU_OP.CERTAINTY,
        MENU_OP.ADD_NOTE,
        MENU_OP.EDIT,
        MENU_OP.DELAY,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.DOWNLOAD_PDF,
        MENU_OP.SEND_MAIL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.CANCEL,
        MENU_OP.DELETE
    ],
    [OPERATION_TYPE.SERVICE]: [
        MENU_OP.DETAIL,
        MENU_OP.SAVE_OPERATION,
        MENU_OP.SEND_TO_VESSEL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.PUSH_CLOSER,
        MENU_OP.CERTAINTY,
        MENU_OP.ADD_NOTE,
        MENU_OP.EDIT,
        MENU_OP.DELAY,
        MENU_OP.DUPLICATE,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.DOWNLOAD_PDF,
        MENU_OP.SEND_MAIL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.CANCEL,
        MENU_OP.DELETE
    ],
    [OPERATION_TYPE.SUPPORT]: [
        MENU_OP.DETAIL,
        MENU_OP.SAVE_OPERATION,
        MENU_OP.SEND_TO_VESSEL,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.PUSH_CLOSER,
        MENU_OP.EDIT,
        MENU_CALENDAR.DIVIDE,
        MENU_OP.DELETE
    ],
}

export type TMenuCalendar = Partial<Record<MENU_CALENDAR, (props: { time: number, vessel: IVessel, dispatch: Dispatch }) => ItemType>>

export const menuCalendar: TMenuCalendar = {
    [MENU_CALENDAR.ADD_EVENT]: ({ time, vessel, dispatch }) => {
        const onClick = () => {
            const start = Math.round(time / TIME_PER_STEP) * TIME_PER_STEP;
            dispatch(planOpActions.togglePopupOperation({
                isShow: true,
                operationType: OPERATION_TYPE.EVENT,
                editMode: ACTION_TYPE.CREATE,
                values: {
                    operation: {
                        operation_type: OPERATION_TYPE.EVENT,
                        note: "",
                        certain_mode: CERTAIN_TYPE.NONE,
                        pois: [],
                        checklist_ids: [],
                    },
                    activity_log: [{ est_start_time: start }, { est_start_time: null }],
                    vessel
                }
            }))
        }
        return {
            key: MENU_CALENDAR.ADD_EVENT,
            label: <div>Add event</div>,
            onClick,
        }
    },
    [MENU_CALENDAR.ADD_AVAILABLE_TIME]: ({ time, vessel }) => {
        const onClick = (date: number, vessel: IVessel) => {
            const state = getStore();
            const { operations, level } = selectPlan.root(state);
            const opMode = selectOpMode(state);
            const start = getStartTimeFromDate({ date, operations, level, opMode, vessel });

            const el = openPopup(<PopupAvailableTime
                visible={true}
                editMode={ACTION_TYPE.CREATE}
                availableTime={{ start_time: start }}
                vessel={vessel}
                onClose={() => el.remove()}
            />)
        };
        return {
            key: MENU_CALENDAR.ADD_AVAILABLE_TIME,
            label: <div>Add available time</div>,
            onClick: () => onClick(time, vessel),
        }
    },
    [MENU_CALENDAR.ADD_SERVICE]: ({ time, vessel, dispatch }) => {
        const onClick = () => {
            const startTime = Math.round(time / TIME_PER_STEP) * TIME_PER_STEP;
            const params: Partial<PlanOperationState['popupOperation']> = funcGlobal[FuncOp.BeginCreate]({
                vessel,
                startTime,
                defaultOpType: OPERATION_TYPE.SERVICE,
            })
            dispatch(planOpActions.togglePopupOperation({
                isShow: true,
                operationType: OPERATION_TYPE.SERVICE,
                editMode: ACTION_TYPE.CREATE,
                values: params.values,
            }))
        }
        return {
            key: MENU_CALENDAR.ADD_SERVICE,
            label: <div>Add service operation</div>,
            onClick,
        }
    }
}

export enum MENU_AVAILABLE_TIME {
    EDIT = 'edit-available-time',
    DELETE = 'delete-available-time',
}

export type TMenuAvailableTime = Partial<Record<MENU_AVAILABLE_TIME, (props: { onClick: any }) => { isActive: boolean } & ItemType>>

export const menuAvailableTime: TMenuAvailableTime = {
    [MENU_AVAILABLE_TIME.EDIT]: ({ onClick }) => {
        return {
            key: MENU_AVAILABLE_TIME.EDIT,
            label: <div className='menu-line'>
                <Icon icon={ICON_NAME.EDIT} />
                <span>Edit available time</span>
            </div>,
            onClick,
            isActive: true
        }
    },
    [MENU_AVAILABLE_TIME.DELETE]: ({ onClick }) => {
        return {
            key: MENU_AVAILABLE_TIME.DELETE,
            label: <div className='menu-line' data-danger='true'>
                <Icon icon={ICON_NAME.DELETE} danger />
                <span>Delete available time</span>
            </div>,
            onClick,
            isActive: true
        }
    }
}

const renderOpt = (data: IMenuOperation) => {
    return data.isActive ? {
        key: data.name,
        label: <div className='menu-line' data-danger={data.danger}>
            <Icon icon={data.icon} size={data.iconSize || 15} style={data.iconStyle} danger={data.danger} />
            <span>{data.name}</span>
        </div>,
        onClick: data.onClick,
    } : null
};

export const generateMenu = (data: IDataMenuOperation, props: IPropsMenu): TMenuOp[] => {
    const { operation_type } = data.operation;
    return (
        menuOptions[operation_type]?.reduce((rs: TMenuOp[], type) => {
            const value = menuOp[type]?.(data, props) || menuCommon[type]?.() || null;

            if (
                value?.type === 'divider' &&
                (!rs.length || rs[rs.length - 1]?.type === 'divider')
            ) {
                return rs;
            }

            if (value) {
                rs.push(value);
            }

            return rs;
        }, []) || []
    ).filter((item, index, arr) => {
        return !(item?.type === 'divider' && index === arr.length - 1);
    });
}
